/** Where the value ended up, and where the caret belongs after the write. */
export interface TokenInsertion {
  value: string;
  caret: number;
}

/**
 * Writes a token into a text value at the caret, or appends it when there is
 * none.
 *
 * The append branch is not a fallback nobody reaches: a chip pressed before the
 * field was ever focused reports no selection at all, and that is the ordinary
 * first interaction. It adds the separating space the caret branch must never
 * add — inserting mid-sentence has to put exactly what was asked for and
 * nothing else, or the operator is fighting the control.
 *
 * A backwards drag reports `selectionStart > selectionEnd`, and a selection
 * left over from before a programmatic reset can point past the end; both are
 * normalised here rather than at the two call sites.
 */
export function insertToken(
  value: string,
  selectionStart: number | null,
  selectionEnd: number | null,
  token: string,
): TokenInsertion {
  if (selectionStart === null || selectionEnd === null) {
    const separator = value === '' || /\s$/u.test(value) ? '' : ' ';
    const appended = `${value}${separator}${token}`;

    return { value: appended, caret: appended.length };
  }

  const clamp = (index: number) => Math.min(Math.max(index, 0), value.length);
  const start = Math.min(clamp(selectionStart), clamp(selectionEnd));
  const end = Math.max(clamp(selectionStart), clamp(selectionEnd));

  return {
    value: `${value.slice(0, start)}${token}${value.slice(end)}`,
    caret: start + token.length,
  };
}
